import { useEffect, useState } from "react";
import { Users, CreditCard, DollarSign, Activity } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, AreaChart, Area, CartesianGrid } from "recharts";
import { RecentPurchases } from "@/components/admin/RecentPurchases";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";

const TICK = { fontSize: 11, fill: "var(--mm-text-secondary)" };
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
export const usd = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const StatCard = ({ icon: I, label, value, sub, color, testId }) => (
  <div className="mm-card p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-mm-muted font-medium">{label}</span>
      <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: color }}><I size={18} /></span>
    </div>
    <div className="font-display text-3xl font-bold mt-3" data-testid={`${testId}-value`}>{value}</div>
    {sub && <div className="text-xs text-mm-muted mt-1">{sub}</div>}
  </div>
);

export const AdminStats = ({ refreshKey, includeTest }) => {
  const [s, setS] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/admin/stats", { params: { include_test: includeTest } }).then((r) => setS(r.data)).catch((e) => logError("Admin stats failed:", e));
  }, [refreshKey, tick, includeTest]);

  if (!s) return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">{[0, 1, 2, 3].map((i) => <div key={i} className="mm-card h-32 animate-pulse" />)}</div>;
  const providers = Object.entries(s.revenue.by_provider).map(([k, v]) => `${k} ${usd(v.total)}`).join(" · ");

  return (
    <div className="mb-6" data-testid="admin-stats">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mm-stagger">
        <StatCard testId="admin-stat-total-users" icon={Users} color="var(--mm-primary)" label="Total users" value={s.total_users} sub={`+${s.new_users_7d} this week`} />
        <StatCard testId="admin-stat-paid" icon={CreditCard} color="var(--mm-secondary)" label="Paid signups" value={s.paid_users} sub={`${s.conversion_rate}% conversion · +${s.paid_signups_7d} this week`} />
        <StatCard testId="admin-stat-revenue" icon={DollarSign} color="var(--mm-success)" label="Revenue" value={usd(s.revenue.total)} sub={providers || `${s.revenue.purchases} purchases`} />
        <StatCard testId="admin-stat-wau" icon={Activity} color="var(--mm-tertiary)" label="Weekly active users" value={s.weekly_active_users} sub={`${s.checkins_7d} check-ins in 7 days`} />
      </div>
      <div className="mm-card p-5 mt-4">
        <h3 className="font-semibold mb-3">Revenue · last 12 months</h3>
        <div className="h-56" data-testid="admin-revenue-chart">
          <ResponsiveContainer>
            <AreaChart data={s.revenue_monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--mm-border)" />
              <XAxis dataKey="month" tick={TICK} tickFormatter={monthLabel} />
              <YAxis tick={TICK} tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(v, n) => (n === "Revenue" ? usd(v) : v)} labelFormatter={monthLabel} />
              <Area type="monotone" dataKey="revenue" name="Revenue" stroke="var(--mm-success)" fill="var(--mm-success)" fillOpacity={0.2} strokeWidth={2.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="mm-card p-5 mt-4">
        <h3 className="font-semibold mb-3">Signups · last 14 days</h3>
        <div className="h-52" data-testid="admin-signups-chart">
          <ResponsiveContainer>
            <BarChart data={s.signups_series}>
              <XAxis dataKey="date" tick={TICK} tickFormatter={(d) => d.slice(5)} />
              <YAxis allowDecimals={false} tick={TICK} />
              <Tooltip />
              <Legend />
              <Bar dataKey="signups" name="Signups" fill="var(--mm-primary)" radius={[6, 6, 0, 0]} />
              <Bar dataKey="paid" name="Paid" fill="var(--mm-success)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <RecentPurchases items={s.recent_purchases} onChange={() => setTick((t) => t + 1)} />
    </div>
  );
};
